if (!customElements.get('product-video-modal')) {
  customElements.define(
    'product-video-modal',
    class ProductVideoModal extends HTMLElement {
      constructor() {
        super();
        this.drawerObserver = null;
        this.resizeObserver = null;
        this.activeIndex = null;
        this.playRequest = 0;
        this.handleClick = this.handleClick.bind(this);
        this.handleResize = this.handleResize.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.handleClick);
        window.addEventListener('resize', this.handleResize);
        this.observeDrawer();

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.handleResize);
          this.querySelectorAll('.cyril-video-picker__player-item').forEach((item) => {
            this.resizeObserver.observe(item);
          });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        window.removeEventListener('resize', this.handleResize);
        this.pauseAllMedia();
        this.drawerObserver?.disconnect();
        this.resizeObserver?.disconnect();
      }

      observeDrawer() {
        const drawer = this.closest('side-drawer');
        if (!drawer) return;

        this.drawerObserver = new MutationObserver(() => {
          const isOpen = drawer.classList.contains('open') || drawer.classList.contains('active');
          if (!isOpen) this.reset();
          else this.loadPlayerLibrary().catch(() => {});
        });

        this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }

      refresh() {
        this.reset();
      }

      reset() {
        this.pauseAllMedia();
        this.activeIndex = null;
        this.playRequest += 1;
        this.classList.remove('is-player');
        this.closest('.cyril-video-picker__content')?.classList.remove('is-player');

        const selectView = this.querySelector('[data-video-select-view]');
        const playerView = this.querySelector('[data-video-player-view]');

        selectView?.classList.remove('hidden');
        playerView?.classList.add('hidden');
        playerView?.setAttribute('aria-hidden', 'true');

        this.getPlayerItems().forEach((item) => {
          item.classList.add('hidden');
          item.setAttribute('aria-hidden', 'true');
        });
      }

      handleClick(event) {
        const openButton = event.target.closest('[data-video-open]');
        if (openButton && this.contains(openButton)) {
          event.preventDefault();
          const index = this.getVisibleCards().indexOf(openButton);
          if (index >= 0) this.openPlayer(index);
          return;
        }

        const backButton = event.target.closest('[data-video-back]');
        if (backButton && this.contains(backButton)) {
          event.preventDefault();
          const previous = this.getVisibleCards()[this.activeIndex];
          this.reset();
          previous?.focus();
        }
      }

      openPlayer(index) {
        const items = this.getVisiblePlayerItems();
        const item = items[index];
        if (!item) return;

        this.pauseAllMedia();
        this.activeIndex = index;
        this.classList.add('is-player');
        this.closest('.cyril-video-picker__content')?.classList.add('is-player');

        const selectView = this.querySelector('[data-video-select-view]');
        const playerView = this.querySelector('[data-video-player-view]');

        selectView?.classList.add('hidden');
        playerView?.classList.remove('hidden');
        playerView?.setAttribute('aria-hidden', 'false');

        this.getPlayerItems().forEach((playerItem) => {
          const active = playerItem === item;
          playerItem.classList.toggle('hidden', !active);
          playerItem.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        this.updateFrameSize(item);
        this.playItem(item);
        this.querySelector('[data-video-back]')?.focus();
      }

      getVisibleCards() {
        return Array.from(this.querySelectorAll('.cyril-video-picker__card:not(.hidden)'));
      }

      getPlayerItems() {
        return Array.from(this.querySelectorAll('[data-video-player-item]'));
      }

      getVisiblePlayerItems() {
        return this.getPlayerItems().filter((item) => !item.classList.contains('hidden-by-filter'));
      }

      handleResize() {
        const item = this.querySelector('[data-video-player-item]:not(.hidden)');
        if (item) this.updateFrameSize(item);
      }

      updateFrameSize(item) {
        const frame = item.querySelector('.cyril-video-picker__frame');
        if (!frame) return;

        const rect = item.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const styles = window.getComputedStyle(item);
        const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
        const paddingY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
        const availableWidth = Math.max(1, rect.width - paddingX);
        const controlsHeight = item.querySelector('[data-video-controls]:not([hidden])')?.getBoundingClientRect().height || 0;
        const availableHeight = Math.max(1, rect.height - paddingY - controlsHeight);
        const ratio = this.getItemRatio(item);

        let fitWidth = availableWidth;
        let fitHeight = availableWidth / ratio;

        if (fitHeight > availableHeight) {
          fitHeight = availableHeight;
          fitWidth = availableHeight * ratio;
        }

        frame.style.setProperty('--video-fit-width', `${Math.floor(fitWidth)}px`);
        frame.style.setProperty('--video-fit-height', `${Math.floor(fitHeight)}px`);
      }

      getItemRatio(item) {
        const ratio = parseFloat(item.dataset.videoRatio || '1.777');
        return Number.isFinite(ratio) && ratio > 0 ? ratio : 1.777;
      }

      loadPlayerLibrary() {
        if (window.cyrilVideoLibraryPromise) return window.cyrilVideoLibraryPromise;
        const load = (type, url) => new Promise((resolve, reject) => {
          const element = document.createElement(type);
          const timer = window.setTimeout(() => finish(new Error('Player loading timed out')), 20000);
          const finish = (error) => {
            window.clearTimeout(timer);
            element.onload = element.onerror = null;
            if (error) { element.remove(); reject(error); }
            else resolve();
          };
          element.onload = () => finish();
          element.onerror = () => finish(new Error('Player asset unavailable'));
          if (type === 'script') { element.src = url; element.async = true; }
          else { element.rel = 'stylesheet'; element.href = url; }
          document.head.append(element);
        });
        window.cyrilVideoLibraryPromise = Promise.all([
          window.videojs ? Promise.resolve() : load('script', this.dataset.playerScript),
          load('link', this.dataset.playerStyle)
        ]).catch((error) => {
          window.cyrilVideoLibraryPromise = null;
          throw error;
        });
        return window.cyrilVideoLibraryPromise;
      }

      async playItem(item) {
        const host = item.querySelector('[data-hosted-video]');
        if (!host) {
          const media = item.querySelector('deferred-media');
          media?.loadContent?.(false);
          media?.playMedia?.();
          return;
        }
        const request = ++this.playRequest;
        const status = host.querySelector('[data-video-status]');
        const failure = host.querySelector('[data-video-failure]');
        failure.hidden = true;
        status.hidden = false;
        host.querySelector('[data-video-retry]').onclick = () => this.playItem(item);
        try {
          await this.loadPlayerLibrary();
        } catch (error) {
          if (request !== this.playRequest || !this.isConnected) return;
          status.hidden = true;
          failure.hidden = false;
          return;
        }
        if (request !== this.playRequest || !this.isConnected || item.classList.contains('hidden')) return;
        const mount = host.querySelector('[data-video-mount]');
        mount.replaceChildren(host.querySelector('template').content.cloneNode(true));
        const video = mount.querySelector('video');
        const quality = host.querySelector('[data-video-quality]');
        quality.selectedIndex = 0;
        let qualityMenu;
        this.productData ||= fetch(`${host.dataset.productUrl}.js`).then((response) => {
          if (!response.ok) throw new Error('Product video sources unavailable');
          return response.json();
        }).catch(() => null);
        this.productData.then((product) => {
          const media = product?.media?.find((entry) => String(entry.id) === host.dataset.mediaId);
          const sources = (media?.sources || []).filter((source) => source.format === 'mp4').sort((a, b) => b.height - a.height);
          sources.forEach((source) => {
            if ([...quality.options].some((option) => option.text === `${source.height}p`)) return;
            const option = new Option(`${source.height}p`, source.url);
            option.dataset.type = source.mime_type;
            quality.add(option);
          });
          if (qualityMenu && !qualityMenu.isDisposed()) qualityMenu.update();
        });
        video.querySelectorAll('source').forEach((source) => source.remove());
        const initialSource = quality.selectedOptions[0];
        const player = window.videojs(video, {
          sources: [{ src: initialSource.value, type: initialSource.dataset.type }],
          controls: true, autoplay: false, muted: true, playsinline: true,
          preload: 'auto', loop: host.dataset.loop === 'true',
          enableSmoothSeeking: true, disableSeekWhileScrubbingOnMobile: true,
          html5: { vhs: { overrideNative: false } },
          controlBar: { pictureInPictureToggle: false, subsCapsButton: false, captionsButton: false, subtitlesButton: false, descriptionsButton: false, volumePanel: { inline: false } }
        });
        this.player = player;
        let switching = false;
        let sourceRevision = 0;
        const loading = () => { if (failure.hidden) status.hidden = false; };
        const settled = () => { if (!switching) status.hidden = true; };
        player.on(['loadstart', 'waiting', 'seeking'], loading);
        player.on(['playing', 'seeked', 'ended'], settled);
        player.on('canplay', () => { if (player.paused()) settled(); });
        player.on('pause', settled);
        player.on('error', () => { status.hidden = true; failure.hidden = false; });
        const play = () => {
          const promise = player.play();
          promise?.catch(() => {
            if (player.isDisposed()) return;
            status.hidden = true;
            player.hasStarted(false);
          });
        };
        const changeSource = (resumeTime = 0, resumePlayback = true) => {
          const revision = ++sourceRevision;
          const option = quality.selectedOptions[0];
          switching = true;
          failure.hidden = true;
          status.hidden = false;
          player.one('loadedmetadata', () => {
            if (player.isDisposed() || revision !== sourceRevision) return;
            switching = false;
            if (resumeTime > 0) player.currentTime(Math.min(resumeTime, Math.max(0, player.duration() - 0.1)));
            if (resumePlayback) play();
            else { player.pause(); status.hidden = true; }
          });
          player.src({ src: option.value, type: option.dataset.type });
          if (resumePlayback) play();
          else player.load();
        };
        quality.onchange = () => changeSource(player.currentTime(), !player.paused());
        const MenuButton = window.videojs.getComponent('MenuButton');
        const MenuItem = window.videojs.getComponent('MenuItem');
        class QualityItem extends MenuItem {
          handleClick(event) {
            super.handleClick(event);
            this.options_.choose();
          }
        }
        class QualityMenu extends MenuButton {
          constructor(owner, options) {
            super(owner, options);
            this.addClass('cyril-vjs-quality');
            this.controlText(quality.getAttribute('aria-label'));
            this.menuButton_.el().querySelector('.vjs-icon-placeholder').classList.add('vjs-icon-cog');
          }
          createItems() {
            return [...quality.options].map((option) => new QualityItem(this.player_, {
              label: option.text, selectable: true, selected: option.selected,
              choose: () => {
                if (!option.selected) {
                  quality.value = option.value;
                  quality.onchange();
                }
                this.items.forEach((item, index) => item.selected(quality.options[index].selected));
                this.unpressButton();
              }
            }));
          }
        }
        qualityMenu = new QualityMenu(player, { title: quality.getAttribute('aria-label') });
        const fullscreenIndex = player.controlBar.children().indexOf(player.controlBar.getChild('fullscreenToggle'));
        player.controlBar.addChild(qualityMenu, {}, fullscreenIndex);

        host.querySelector('[data-video-retry]').onclick = () => changeSource(player.currentTime(), true);
        player.ready(play);
      }

      pauseAllMedia() {
        this.playRequest += 1;
        if (this.player && !this.player.isDisposed()) this.player.dispose();
        this.player = null;
        this.querySelectorAll('[data-hosted-video]').forEach((host) => {
          host.querySelectorAll('video').forEach((video) => { video.pause(); video.removeAttribute('src'); video.load(); });
          host.querySelector('[data-video-mount]').replaceChildren();
          host.querySelector('[data-video-status]').hidden = true;
        });
        this.pauseMediaInside(this);
      }

      pauseMediaInside(root) {
        root.querySelectorAll('deferred-media').forEach((media) => {
          if (typeof media.pauseMedia === 'function') media.pauseMedia();
        });

        root.querySelectorAll('video').forEach((video) => {
          try {
            video.pause();
          } catch (error) {
            // Ignore media state errors.
          }
        });

        root.querySelectorAll('iframe[data-video-type]').forEach((iframe) => {
          const type = iframe.dataset.videoType;
          const message = type === 'youtube'
            ? '{"event":"command","func":"pauseVideo","args":""}'
            : '{"method":"pause"}';
          iframe.contentWindow?.postMessage(message, '*');
        });
      }
    }
  );
}
